import { SetupChecklist } from "../onboarding/onboarding";
import "./dashboard.css";
import "./widgets/widgets.css";
import { HolidayRatesNotice } from '@src/components/holiday/holidayRatesNotice';
import { MinimumWageNotice } from '@src/components/payroll/minimumWageNotice';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Card, Avatar, Dropdown, Menu, Button, DatePicker, Skeleton, Input, Tag, Empty } from 'antd';
import { 
  TeamOutlined, 
  EnvironmentOutlined, 
  DownOutlined,
  CalendarOutlined,
  FileTextOutlined,
  WarningOutlined,
  CloseOutlined,
  FormOutlined,
  BookOutlined,
  DeploymentUnitOutlined,
  BranchesOutlined
} from '@ant-design/icons';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Aperture, Bell, Clock, Cog, FileClockIcon, LogOut, Search, Table, Users2 } from "lucide-react";
import { useLazyQuery, useMutation, useQuery } from "@apollo/client";
import { getActiveUserQuery, getActiveUserDetailQuery, getActivityLogQuery, getAppUsageForDashboardQuery, getEmployeeDashboardDetailQuery, getOpenDutiesQuery, getPendingCheckpointDetailQuery, getPendingReportsForDashboardQuery, getPendingTaskForDashboardQuery, getPendingTaskDetailQuery, getScheduleDashboardDetailQuery, getTotalDeployStaffQuery, getTotalguardingDutiesGraphQuery, getTotalguardingDutiesQuery, getTotalHourQuery } from "../../util/query/dashboard.query";
import dayjs from "dayjs";
import utc from 'dayjs/plugin/utc';
import weekOfYear from 'dayjs/plugin/weekOfYear';
import isoWeek from 'dayjs/plugin/isoWeek';
import DashboardHeader from "./dashboardHeader";
import { deactiveSessionMutation } from '@src/util/mutation/userAccess.mutation';
import { useNavigate } from "react-router-dom";
import { logoutQuery } from '@src/util/query/logout.query';
import { resetAllReducerState } from "../../middleware/redux/resetAll";
import ActivityItem from "../../components/activityItems/activityItems";
import SkeletonAvatar from "antd/es/skeleton/Avatar";
import InfiniteScroll from "react-infinite-scroll-component";
import { useDashboardTileDetail } from "./useDashboardTileDetail";
import { DashboardDetailDrawer } from "./dashboardDetailDrawer";
import { FulfillDutyAction } from "./fulfillDutyAction";
import { getDataQualityReportMutation } from "@src/util/mutation/systemReport.mutation";
import { useTenantFeatureFlags } from "@src/hooks/useTenantFeatureFlags";
import { ComplianceDashboard } from "./complianceDashboard";
import { useSelector } from "react-redux";
import { getUniformRequestsQuery } from "@src/util/query/uniform.query";
import { getDutyRequestsQuery } from "@src/util/query/dutyRequest.query";
import { UniformRequestsModal } from "@src/components/uniform/uniformRequestsModal";
import { ServiceRequestsModal } from "@src/pages/customerRequests/serviceRequestsModal";
import { socket } from "@src/config/socket";
import { SortableTiles } from "./sortableTiles";
import { getMyDashboardLayoutQuery, saveMyDashboardLayoutMutation } from "@src/util/query/dashboardLayout.query";
import { DashboardContext } from "./widgets/widgetShell";
import { useDetailHost } from "./widgets/dashboardDetailHost";
import { CustomDashboard, DashboardBar } from "./widgets/customDashboard";
import { useMyDashboards } from "./widgets/useMyDashboards";
import { TEMPLATES, templateWidgets } from "./widgets/catalog";
import { dutyOfRowHref, dutyRowHref } from "./dutyLink";



dayjs.extend(utc);
dayjs.extend(weekOfYear);
dayjs.extend(isoWeek);

const { RangePicker } = DatePicker;

// small "nothing to plot" placeholder for the metric-card sparklines, so an
// empty period shows a message instead of a blank chart box
const SparkEmpty = ({ height = 50 }) => (
  <div style={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#bbb', fontSize: 11 }}>
    No data for this period
  </div>
);

// number of activity log rows fetched per scroll page - keeps the initial
// load fast while still letting the user scroll through the full day
const ACTIVITY_LOG_PAGE_SIZE = 50;

const ActivityLogCard = React.memo(({ dateFilter, dateFilterLabel, onDateFilterChange, getDateFromFilter }) => {
  const [activityActionFilter, setActivityActionFilter] = useState('all');
  const [activityEntityFilter, setActivityEntityFilter] = useState('all');
  const [activitySearchTags, setActivitySearchTags] = useState([]);
  const [activitySearchInput, setActivitySearchInput] = useState('');
  const [activitySearchVisible, setActivitySearchVisible] = useState(false);
  const activitySearchRef = useRef(null);

  const [getActivityLog, { loading: activityLogLoading }] = useLazyQuery(getActivityLogQuery);

  // accumulated rows across every page loaded so far for the current date filter
  const [allActivityLogs, setAllActivityLogs] = useState([]);
  const [activityLogHasMore, setActivityLogHasMore] = useState(true);
  // tracks which page the in-flight/most recent response belongs to, set
  // synchronously (ahead of state) so a stale/out-of-order response can be dropped
  const activityLogPageRef = useRef(1);

  // merges straight off the settled query result rather than a useEffect
  // watching the hook's `data` - Apollo's cache can hand back the exact same
  // `data` reference when a fresh response normalizes to something deeply
  // equal to what's already cached (e.g. flipping back to a filter you just
  // viewed), which would leave a useEffect([data]) never re-firing
  const fetchActivityLogPage = useCallback(async (page) => {
    if (!dateFilter) return;
    const { startDate, endDate } = getDateFromFilter(dateFilter);
    activityLogPageRef.current = page;
    const result = await getActivityLog({
      fetchPolicy: 'network-only',
      variables: {
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
        page,
        pageSize: ACTIVITY_LOG_PAGE_SIZE,
      }
    });

    const response = result?.data?.getActivityLog?.response;
    if (!response) return;
    if (activityLogPageRef.current !== page) return;

    const totalCount = result?.data?.getActivityLog?.totalCount ?? 0;
    setAllActivityLogs(prev => {
      const merged = page === 1 ? response : [...prev, ...response];
      setActivityLogHasMore(merged.length < totalCount);
      return merged;
    });
  }, [dateFilter, getDateFromFilter, getActivityLog]);

  // date filter changed (today/weekly/monthly/custom) - start over from page 1
  useEffect(() => {
    if (dateFilter) {
      setAllActivityLogs([]);
      setActivityLogHasMore(true);
      fetchActivityLogPage(1);
    }
  }, [dateFilter, getDateFromFilter]);

  const loadMoreActivityLogs = useCallback(() => {
    if (activityLogLoading) return;
    fetchActivityLogPage(activityLogPageRef.current + 1);
  }, [activityLogLoading, fetchActivityLogPage]);

  useEffect(() => {
    if (activitySearchVisible) {
      setTimeout(() => activitySearchRef.current?.focus(), 50);
    }
  }, [activitySearchVisible]);

  const filteredActivityLogs = allActivityLogs.filter(d => {
    const actionMatch = activityActionFilter === 'all' || d?.action === activityActionFilter;
    const entityMatch = activityEntityFilter === 'all' || d?.entity?.trim() === activityEntityFilter;
    const searchMatch = activitySearchTags.length === 0 ||
      activitySearchTags.some(tag => (d?.target || '').toLowerCase().includes(tag.toLowerCase()));
    return actionMatch && entityMatch && searchMatch;
  });

  const activityEntities = [...new Set(allActivityLogs.map(d => d?.entity?.trim()))].filter(Boolean);

  const dateFilterMenu = (
    <Menu onClick={({ key }) => onDateFilterChange(key)}>
      <Menu.Item key="today">Today</Menu.Item>
      <Menu.Item key="weekly">Weekly</Menu.Item>
      <Menu.Item key="monthly">Monthly</Menu.Item>
      <Menu.Item key="custom">Custom Dates</Menu.Item>
    </Menu>
  );

  const actionFilterMenu = (
    <Menu onClick={({ key }) => setActivityActionFilter(key)}>
      <Menu.Item key="all">All Actions</Menu.Item>
      <Menu.Item key="CREATE">Create</Menu.Item>
      <Menu.Item key="UPDATE">Update</Menu.Item>
      <Menu.Item key="DELETE">Delete</Menu.Item>
      <Menu.Item key="VIEW">View</Menu.Item>
    </Menu>
  );

  const entityFilterMenu = (
    <Menu onClick={({ key }) => setActivityEntityFilter(key)}>
      <Menu.Item key="all">All Entities</Menu.Item>
      {activityEntities.map(entity => (
        <Menu.Item key={entity}>{entity}</Menu.Item>
      ))}
    </Menu>
  );

  return (
    <Card
      className="activity-card"
      size="small"
      style={{ height: 'auto', minHeight: '400px', maxHeight: 'clamp(500px, 88vh, 900px)', display: 'flex', flexDirection: 'column', borderRadius:'14px' }}
      bodyStyle={{ padding: 0, display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}
    >
      {/* ── fixed header + filters ── */}
      <div style={{ flexShrink: 0, padding: '12px 12px 8px', borderBottom: '1px solid #f0f0f0', background: '#fff', borderRadius:'14px' }}>
        <div className="chart-header">
          <span className="chart-title">Activity Logs</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span
              onClick={() => {
                setActivitySearchVisible(v => !v);
                if (activitySearchVisible) setActivitySearchInput('');
              }}
              style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', color: activitySearchVisible || activitySearchTags.length > 0 ? '#126E7D' : '#8c8c8c' }}
            >
              <Search size={14} />
            </span>
            <Dropdown overlay={dateFilterMenu} trigger={['click']}>
              <span className="metric-badge clickable">
                {dateFilterLabel} <DownOutlined style={{ fontSize: '10px' }} />
              </span>
            </Dropdown>
          </div>
        </div>

        {activitySearchVisible && (
          <div style={{ marginTop: '8px' }}>
            <Input
              ref={activitySearchRef}
              placeholder="Type a name and press Enter..."
              value={activitySearchInput}
              size="small"
              onChange={e => setActivitySearchInput(e.target.value)}
              onPressEnter={() => {
                const val = activitySearchInput.trim();
                if (val && !activitySearchTags.includes(val)) {
                  setActivitySearchTags(prev => [...prev, val]);
                }
                setActivitySearchInput('');
              }}
              onKeyDown={e => {
                if (e.key === 'Escape') {
                  setActivitySearchVisible(false);
                  setActivitySearchInput('');
                }
              }}
              onBlur={() => {
                if (!activitySearchInput.trim()) setActivitySearchVisible(false);
              }}
            />
          </div>
        )}

        {activitySearchTags.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '8px' }}>
            {activitySearchTags.map(tag => (
              <Tag
                key={tag}
                closable
                onClose={() => setActivitySearchTags(prev => prev.filter(t => t !== tag))}
                style={{ borderRadius: '12px', fontSize: '11px' }}
                color="cyan"
              >
                {tag}
              </Tag>
            ))}
          </div>
        )}

        <div style={{ display: 'flex', gap: '8px', marginTop: '8px', flexWrap: 'wrap' }}>
          <Dropdown overlay={actionFilterMenu} trigger={['click']}>
            <span className="metric-badge clickable">
              {activityActionFilter === 'all' ? 'All Actions' : activityActionFilter} <DownOutlined style={{ fontSize: '10px' }} />
            </span>
          </Dropdown>
          <Dropdown overlay={entityFilterMenu} trigger={['click']}>
            <span className="metric-badge clickable">
              {activityEntityFilter === 'all' ? 'All Entities' : activityEntityFilter} <DownOutlined style={{ fontSize: '10px' }} />
            </span>
          </Dropdown>
        </div>
      </div>

      {/* ── scrollable log list ── */}
      <div id="activity-log-scroll-container" style={{ flex: 1, overflow: 'auto', padding: '8px 12px 12px' }}>
        {activityLogLoading && allActivityLogs.length === 0 ? (
          <Skeleton avatar active paragraph={{ rows: 4 }} />
        ) : filteredActivityLogs.length === 0 ? (
          <div style={{ color: 'gray', textAlign: 'center', padding: '24px 0' }}>No activity logs found.</div>
        ) : (
          <InfiniteScroll
            dataLength={filteredActivityLogs.length}
            next={loadMoreActivityLogs}
            hasMore={activityLogHasMore}
            scrollableTarget="activity-log-scroll-container"
            loader={
              <div style={{ padding: '8px 0' }}>
                <Skeleton avatar active paragraph={{ rows: 2 }} />
              </div>
            }
            endMessage={
              <div style={{ color: '#bbb', textAlign: 'center', padding: '12px 0', fontSize: '12px' }}>
                No more logs
              </div>
            }
          >
            {filteredActivityLogs.map((d, index) => (
              <ActivityItem
                key={index}
                user={d?.target}
                action={d?.action}
                target={d?.recordLabel}
                location={d?.entity}
                time={d?.date}
                change={d?.changes}
              />
            ))}
          </InfiniteScroll>
        )}
      </div>
    </Card>
  );
});

export const Dashboard = () => {
  // which dashboard: "Operations" or "Compliance" - remembered once picked (see `dashboardView`
  // below for the default when nothing has been picked yet)
  const [dashboardFilter, setDashboardFilter] = useState(localStorage.getItem("dashboardFilter"));



  // Individual filters for each card
  const [cardFilters, setCardFilters] = useState({
    openDuties: 'today',
    confirmedDuties: 'today',
    unconfirmedDuties: 'today',
    guardingDuties: 'today',
    totalHours: 'today',
    deployedStaff: 'today',
    pendingReports: 'today',
    pendingTasks: 'today',
    appUsers: 'today',
    guardingHoursChart: 'yearly',
    activityLog: 'today'
  });
  
  const [activeCardPicker, setActiveCardPicker] = useState(null);
  const [customDateRange, setCustomDateRange] = useState(null);
  

// Primary Blue: #126E7D
// Derived Very Dim Background Shade: #E8F3F5

  const dimIconBackground = 'rgb(255, 245, 245, 0.9)';


  const navigate = useNavigate();

  // Duty tiles only mean something to tenants that schedule in WFC360. Compliance, appraisals
  // and surveys have their own dashboard (switched to at the top), not tiles on this one.
  const { schedulingEnabled, complianceEnabled, appraisalsEnabled, surveysEnabled, uniformEnabled, holidayEnabled, keyManagementEnabled, mobileResponseEnabled, loading: tenantFlagsLoading } = useTenantFeatureFlags();
  const { authenticatedUserDetail: dashboardUser } = useSelector((state) => state.userAuthReducer);

  // Requests waiting on the office: officers' uniform requests (licensed module) and customers'
  // service requests (part of scheduling). Each tile opens its list in a pop-up to act on.
  const showUniformTile = uniformEnabled && dashboardUser?.permission?.Uniform?.access !== false;
  const showServiceRequestTile = schedulingEnabled && dashboardUser?.permission?.ServiceRequest?.access !== false;
  const [requestsModal, setRequestsModal] = useState(null);
  // counts only (a page of one) - the pop-up loads the lists
  const { data: uniformPendingData, refetch: refetchUniformPending } = useQuery(getUniformRequestsQuery, {
    variables: { filter: { status: ['pending'], pageSize: 1 } },
    skip: !showUniformTile, fetchPolicy: 'network-only',
  });
  const { data: uniformApprovedData, refetch: refetchUniformApproved } = useQuery(getUniformRequestsQuery, {
    variables: { filter: { status: ['approved'], pageSize: 1 } },
    skip: !showUniformTile, fetchPolicy: 'network-only',
  });
  const uniformPending = uniformPendingData?.getUniformRequests?.response?.totalCount || 0;
  const uniformToIssue = uniformApprovedData?.getUniformRequests?.response?.totalCount || 0;
  const refetchUniformWaiting = () => { refetchUniformPending(); refetchUniformApproved(); };
  const { data: serviceRequestData, refetch: refetchServiceRequests } = useQuery(getDutyRequestsQuery, {
    variables: { input: { page: 1, pageSize: 1, status: 'pending' } },
    skip: !showServiceRequestTile, fetchPolicy: 'network-only',
  });
  const serviceRequestsPending = serviceRequestData?.getDutyRequests?.response?.totalCount || 0;

  // This user's own tile order (dragged on the dashboard), saved on the server so it follows
  // them to any browser. Shown at once; a failed save keeps it for this session.
  const { data: layoutData } = useQuery(getMyDashboardLayoutQuery, { variables: { dashboard: 'operations' }, fetchPolicy: 'network-only' });
  const [saveLayout] = useMutation(saveMyDashboardLayoutMutation);
  const [tileOrderOverride, setTileOrderOverride] = useState(null);
  const tileOrder = tileOrderOverride || layoutData?.getMyDashboardLayout?.response?.tileOrder || [];
  const saveTileOrder = (order) => {
    setTileOrderOverride(order);
    saveLayout({ variables: { tileOrder: order, dashboard: 'operations' } }).catch((err) => console.error('[dashboard layout] save failed:', err?.message));
  };

  // Live: realtime's requestCounts module signals whenever a uniform or service request is
  // added or changes (by anyone - the app, a customer, another manager); the counts and any
  // open list are re-read through GraphQL, so permissions and data access still apply.
  const [requestsTick, setRequestsTick] = useState(0);
  const [requestsSubscribeKey, setRequestsSubscribeKey] = useState(0);
  const liveRequests = showUniformTile || showServiceRequestTile;
  useEffect(() => {
    if (!liveRequests) return undefined;
    const onSignal = (data) => {
      if (data?.error) return;
      const kinds = data?.type === 'patch' ? data.kinds || [] : ['uniform', 'service'];
      if (kinds.includes('uniform') && showUniformTile) refetchUniformWaiting();
      if (kinds.includes('service') && showServiceRequestTile) refetchServiceRequests();
      setRequestsTick((t) => t + 1);
    };
    const onReconnect = () => setRequestsSubscribeKey((k) => k + 1);
    socket.on('requestCounts', onSignal);
    socket.on('connect', onReconnect);
    return () => {
      socket.off('requestCounts', onSignal);
      socket.off('connect', onReconnect);
    };
  }, [liveRequests, showUniformTile, showServiceRequestTile]);
  useEffect(() => {
    if (!liveRequests || !dashboardUser?.tenantId) return;
    socket.emit('subscribe', { action: 'subscribe', module: 'requestCounts', param: { tenantId: dashboardUser.tenantId } });
  }, [liveRequests, requestsSubscribeKey, dashboardUser?.tenantId]);
  const showCompliance = complianceEnabled && dashboardUser?.permission?.Compliance?.access !== false;
  const showAppraisals = appraisalsEnabled && dashboardUser?.permission?.Appraisals?.access !== false;
  const showSurveys = surveysEnabled && dashboardUser?.permission?.Surveys?.access !== false;
  const hasComplianceDashboard = !!(showCompliance || showAppraisals || showSurveys);

  // Dashboards the user built themselves (widgets/), next to the standard Operations / Compliance
  // ones. A widget is offered only when the tenant has its module AND the role opens it (only an
  // explicit `false` denies); the server checks the same for each widget's data.
  const myDashboards = useMyDashboards();
  const customDashboard = myDashboards.dashboards.find((d) => d.id === dashboardFilter);
  const [customizing, setCustomizing] = useState(false);
  const [widgetPanelOpen, setWidgetPanelOpen] = useState(false);
  const permission = dashboardUser?.permission;
  // nothing until the company's modules are known (older modules read as on while loading)
  const widgetAccess = React.useMemo(() => (tenantFlagsLoading ? {} : {
    scheduling: !!schedulingEnabled,
    holiday: !!holidayEnabled && permission?.Employee?.access !== false,
    finance: !!schedulingEnabled && permission?.BusinessAnalysis?.access !== false,
    serviceRequests: showServiceRequestTile,
    uniform: showUniformTile,
    compliance: showCompliance,
    appraisals: showAppraisals,
    surveys: showSurveys,
    keys: keyManagementEnabled !== false,
    mobile: mobileResponseEnabled !== false,
    incidents: permission?.SiteIncident?.access !== false,
    complaints: permission?.ComplainRegister?.access !== false,
    attendanceGaps: !!schedulingEnabled && permission?.AttendanceGap?.access !== false,
    punctuality: !!schedulingEnabled && permission?.Timeline?.access !== false,
    licences: permission?.Employee?.access !== false,
  }), [tenantFlagsLoading, schedulingEnabled, holidayEnabled, keyManagementEnabled, mobileResponseEnabled, permission, showServiceRequestTile, showUniformTile, showCompliance, showAppraisals, showSurveys]);

  // The Overview: a standard, director-level dashboard built from widgets (the Overview template,
  // less what this user can't have). Not saved and not editable - a widget's period changes for this
  // visit only; "Make an editable copy" gives them their own.
  const [overviewDashboard, setOverviewDashboard] = useState(null);
  useEffect(() => {
    if (!tenantFlagsLoading && dashboardUser) {
      setOverviewDashboard({ id: 'overview', name: 'Overview Dashboard', template: 'overview', widgets: templateWidgets(TEMPLATES.find((t) => t.key === 'overview'), widgetAccess) });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantFlagsLoading, widgetAccess]);
  const overviewStore = React.useMemo(() => ({
    dashboards: [],
    commit: () => {},
    updateDashboard: (id, change) => setOverviewDashboard((d) => (d ? { ...d, ...(typeof change === 'function' ? change(d) : change) } : d)),
  }), []);
  const standardDashboards = [
    { key: 'Operations', label: 'Operations Dashboard' },
    // schedulingEnabled !== false && { key: 'Overview', label: 'Overview Dashboard' },
    hasComplianceDashboard && { key: 'Compliance', label: 'Compliance Dashboard' },
  ].filter(Boolean);
  const selectDashboard = (key) => {
    setDashboardFilter(key);
    try { localStorage.setItem("dashboardFilter", key); } catch { /* private mode */ }
  };
  // a duty fulfilled from a custom dashboard's drawer re-reads its duty widgets
  const [dutyTick, setDutyTick] = useState(0);
  const widgetDetail = useDetailHost({ onDutyChanged: () => setDutyTick((t) => t + 1) });
  const widgetContext = React.useMemo(() => ({
    ticks: { duties: dutyTick, uniform: requestsTick, service: requestsTick },
    openDetail: widgetDetail.openDetail,
    openRows: widgetDetail.openRows,
    updateRows: widgetDetail.updateRows,
    openRequests: setRequestsModal,
  }), [dutyTick, requestsTick, widgetDetail.openDetail, widgetDetail.openRows, widgetDetail.updateRows]);
  // one of their own (still loading: wait rather than flash Operations); else the one they picked;
  // otherwise Compliance for a tenant that doesn't schedule in WFC360
  const dashboardView = customDashboard ? 'custom'
    : dashboardFilter === 'Overview' && schedulingEnabled !== false ? 'Overview'
    : myDashboards.loading && dashboardFilter && dashboardFilter !== 'Operations' && dashboardFilter !== 'Compliance' ? 'loading'
    : !hasComplianceDashboard ? 'Operations'
    : dashboardFilter === 'Operations' || dashboardFilter === 'Compliance' ? dashboardFilter
      : schedulingEnabled === false ? 'Compliance' : 'Operations';

  const [deactiveSession] = useMutation(deactiveSessionMutation);
  const [logout] = useLazyQuery(logoutQuery);


  const handelLogout = async()=>{
    await deactiveSession({
        variables:{
            deactiveSessionId: localStorage.getItem("deviceId")
        }
    });

    await logout({
        variables: {
            deviceId: localStorage.getItem("deviceId")
        }
    });

    resetAllReducerState();
    localStorage.clear();
    sessionStorage.clear();
    window.location="/"

  };

  const menuItems = [
    { 
      key: 'employee', 
      icon: <Users2 />, 
      title: 'Employees',
      color: dimIconBackground ,
      route: "/user/employee"
    },
    { 
      key: 'site', 
      icon: <EnvironmentOutlined />, 
      title: 'Sites',
      color: dimIconBackground,
      route: "/user/site"
    },
    { 
      key: 'schedule', 
      icon: <Table />, 
      title: 'Schedule',
      color: dimIconBackground,
      route: "/user/schedule"
    },
    { 
      key: 'timeline', 
      icon: <Clock />, 
      title: 'Timeline',
      color: dimIconBackground,
      route: "/user/timeline"
    },
    { 
      key: 'timesheet', 
      icon: <FileClockIcon />, 
      title: 'TimeSheets',
      color: dimIconBackground ,
      route: "/user/timesheet"
    },
    { 
      key: 'branches', 
      icon: <DeploymentUnitOutlined />, 
      title: 'Branches',
      color: dimIconBackground,
      route: "/user/branch"

    },
    { 
      key: 'customers', 
      icon: <TeamOutlined />, 
      title: 'Customers',
      color: dimIconBackground,
      route: "/user/customer" 
    },
    { 
      key: 'site-groups', 
      icon: <EnvironmentOutlined />, 
      title: 'Site Groups',
      color: dimIconBackground,
      route: "/user/sitegroup"
    },
    { 
      key: 'suppliers', 
      icon: <BranchesOutlined />, 
      title: 'Suppliers',
      color: dimIconBackground,
      route: "/user/agency" 
    },
    { 
      key: 'reports', 
      icon: <FileTextOutlined />, 
      title: 'Reports',
      color: dimIconBackground,
      route: "/user/sysreport/favourite"
    },
    { 
      key: 'attendance', 
      icon: <CalendarOutlined />, 
      title: 'Attendance Gap',
      color: dimIconBackground ,
      route: "/user/attandancegap"
    },
    { 
      key: 'incident', 
      icon: <WarningOutlined />, 
      title: 'Incident Reporting',
      color: dimIconBackground,
      route: "/user/incidentReporting"
    },
    { 
      key: 'complaint', 
      icon: <FormOutlined />, 
      title: 'Complaint Register',
      color: dimIconBackground,
      route: "/user/complaint"
    },
    { 
      key: 'task', 
      icon: <BookOutlined />, 
      title: 'Task Library',
      color: dimIconBackground,
      route: "/user/tasklibrary"

    },
    { 
      key: 'post', 
      icon: <Aperture />, 
      title: 'Post',
      color: dimIconBackground,
      route: "/user/employee-detail/"+localStorage.getItem("employeeId")+"/tab/profile/true"
    },
    { 
      key: 'setting', 
      icon: <Cog />, 
      title: 'Setting',
      color: dimIconBackground,
      route: "#/allsetting" 
    },
    { 
      key: 'notification', 
      icon: <Bell />, 
      title: 'Notification',
      color: dimIconBackground,
      route: "/user/notifications" 
    },
    { 
      key: 'logout', 
      icon: <LogOut />, 
      title: 'Logout',
      color: dimIconBackground,
      route: null 
    }
  ];

  const createDateFilterMenu = (cardKey, includeAll = false) => (
    <Menu onClick={({ key }) => {
      if (key === 'custom') {
        setActiveCardPicker(cardKey);
      } else {
        setCardFilters(prev => ({ ...prev, [cardKey]: key }));
        setActiveCardPicker(null);
      }
    }}>
      <Menu.Item key="today">Today</Menu.Item>
      <Menu.Item key="weekly">Weekly</Menu.Item>
      <Menu.Item key="monthly">Monthly</Menu.Item>
      <Menu.Item key="custom">Custom Dates</Menu.Item>
      {includeAll && <Menu.Item key="all">All</Menu.Item>}
    </Menu>
  );


  const createGraphDateFilterMenu = (cardKey) => (
    <Menu onClick={({ key }) => {
      if (key === 'custom') {
        setActiveCardPicker(cardKey);
      } else {
        setCardFilters(prev => ({ ...prev, [cardKey]: key }));
        setActiveCardPicker(null);
      }
    }}>
      <Menu.Item key="weekly">Weekly</Menu.Item>
      <Menu.Item key="monthly">Monthly</Menu.Item>
      <Menu.Item key="yearly">Yearly</Menu.Item>
      <Menu.Item key="custom">Custom Dates</Menu.Item>
    </Menu>
  );

  const getDateFilterLabel = (filter) => {
    const labels = {
      today: 'Today',
      weekly: 'Weekly',
      monthly: 'Monthly',
      yearly: 'Yearly',
      custom: 'Custom',
      all: 'All'
    };
    return labels[filter] || 'Today';
  };


  // open duties query
  const [getOpenDuties, {data:openDutyData, loading: getOpenDutiesLoading, refetch: refetchOpenDuties}] = useLazyQuery(getOpenDutiesQuery);

  const [openDuty, setOpenDuty] = useState({count:0, ids: []});

  useEffect(()=>{
    if(openDutyData?.getOpenDuties?.response &&  openDutyData?.getOpenDuties?.response?.length>0){
      setOpenDuty({
        count: openDutyData?.getOpenDuties?.response[0]?.count,
        ids: openDutyData?.getOpenDuties?.response[0]?.ids,
      })
    }
  },[openDutyData?.getOpenDuties?.response]);



  const [getConfirmDuties, {data:confirmDutyData, loading: getConfirmDutiesLoading, refetch: refetchConfirmDuties}] = useLazyQuery(getOpenDutiesQuery);
  const [confirmDuty, setConfirmDuty] = useState({count:0, ids: []});

  useEffect(()=>{
    if(confirmDutyData?.getOpenDuties?.response &&  confirmDutyData?.getOpenDuties?.response?.length>0){
      setConfirmDuty({
        count: confirmDutyData?.getOpenDuties?.response[0]?.count,
        ids: confirmDutyData?.getOpenDuties?.response[0]?.ids,
      })
    }
  },[confirmDutyData?.getOpenDuties?.response]);

  // Unconfirmed duties: assigned, but the employee hasn't confirmed yet (status "2")
  const [getUnconfirmedDuties, {data:unconfirmedDutyData, refetch: refetchUnconfirmedDuties}] = useLazyQuery(getOpenDutiesQuery);
  const [unconfirmedDuty, setUnconfirmedDuty] = useState({count:0, ids: []});

  useEffect(()=>{
    const response = unconfirmedDutyData?.getOpenDuties?.response;
    if(response){
      // nothing in the period comes back as an empty list - that's 0, not "keep the last figure"
      setUnconfirmedDuty({ count: response[0]?.count || 0, ids: response[0]?.ids || [] });
    }
  },[unconfirmedDutyData?.getOpenDuties?.response]);

  const [getTotalHours, {data:totalHoursData, loading: totalHoursLoading, refetch: refetchTotalHours}] = useLazyQuery(getTotalHourQuery);
  const [totalHours, setTotalHours] = useState(0);
  const [totalHoursIds, setTotalHoursIds] = useState([]);
  const [totalHoursSparkData, setTotalHoursSparkData] = useState([]);

  useEffect(()=>{
    if(totalHoursData?.getTotalHour?.response &&  totalHoursData?.getTotalHour?.response){
      setTotalHours((totalHoursData?.getTotalHour?.response?.totalHours[0]?.grandTotalHours ?? 0).toFixed(2));
      setTotalHoursIds(totalHoursData?.getTotalHour?.response?.totalHours[0]?.ids || []);
      setTotalHoursSparkData(
        totalHoursData?.getTotalHour?.response?.spark?.map((sp)=>({
          value: sp?.totalHoursForBucket?.toFixed(2),
          label: dayjs(sp?.bucketStart).format("MMM YYYY")
        }))
      )
    }
  },[totalHoursData?.getTotalHour?.response]);



  const [getTotalGuardingDuty, {data:totalGuardingDutyData, loading: totalGuardingDutyLoading, refetch: refetchTotalguardingDuty}] = useLazyQuery(getTotalguardingDutiesQuery);
  const [totalGuardHours, setTotalGuardHours] = useState(0);
  const [totalGuardHoursIds, setTotalGuardHoursIds] = useState([]);
  const [totalGuardHoursSparkData, setTotalGuardHoursSparkData] = useState([]);

  useEffect(()=>{
    if(totalGuardingDutyData?.getTotalguardingDuties?.response){
      setTotalGuardHours((totalGuardingDutyData?.getTotalguardingDuties?.response?.totalGuardingDuties[0]?.count ?? 0).toFixed(0));
      setTotalGuardHoursIds(totalGuardingDutyData?.getTotalguardingDuties?.response?.totalGuardingDuties[0]?.ids || []);
      setTotalGuardHoursSparkData(
        totalGuardingDutyData?.getTotalguardingDuties?.response?.spark?.map((sp)=>{
          return ({
            value: sp?.count?.toFixed(2),
            label: dayjs(sp?.bucketStart).format("MMM YYYY")
          });

        })
      )
    }
  },[totalGuardingDutyData?.getTotalguardingDuties?.response]);



  const [getTotalDeployStaff, { data:totalDeployStaff, loading: totalDeployStaffLoading, refetch: refetchGetTotalDeployStaff }] = useLazyQuery(getTotalDeployStaffQuery);
  const [totalDeployedStaff, setTotalDeployedStaff] = useState(0);
  const [totalDeployedStaffIds, setTotalDeployedStaffIds] = useState([]);
  const [totalDeployedStaffSparkData, setTotalDeployedStaffSparkData] = useState([]);

  useEffect(()=>{
    if(totalDeployStaff?.getTotalDeployStaff?.response){
      setTotalDeployedStaff((totalDeployStaff?.getTotalDeployStaff?.response?.totalDeployedStaff[0]?.count ?? 0).toFixed(0));
      setTotalDeployedStaffIds(totalDeployStaff?.getTotalDeployStaff?.response?.totalDeployedStaff[0]?.distinctEmployees || []);
      setTotalDeployedStaffSparkData(
        totalDeployStaff?.getTotalDeployStaff?.response?.spark?.map((sp)=>{
          return ({
            value: sp?.count?.toFixed(2),
            label: dayjs(sp?.bucketStart).format("MMM YYYY")
          });

        })
      )
    }
  },[totalDeployStaff?.getTotalDeployStaff?.response]);



  // pending reports
  const [getPendingReport, {data:pendingReportData, loading: pendingReportLoading, refetch: refetchPendingReports}] = useLazyQuery(getPendingReportsForDashboardQuery);
  const [pendingReport, setPendingReports] = useState({count:0, ids: []});

  useEffect(()=>{
    if(pendingReportData?.getPendingCheckpointsForDashboard?.response){
      // the aggregate underneath this one returns plain {_id} docs rather than
      // grouped raw ids, so normalize both shapes before they're used for the
      // click-through detail drawer
      const rawIds = pendingReportData?.getPendingCheckpointsForDashboard?.response?.ids || [];
      setPendingReports({
        count: pendingReportData?.getPendingCheckpointsForDashboard?.response?.count,
        ids: rawIds.map(id => (id && typeof id === 'object') ? id._id : id),
      })
    };
  },[pendingReportData?.getPendingCheckpointsForDashboard?.response]);

  // pending task
  const [getPendingTask, {data:pendingTaskData, loading: pendingTaskLoading, refetch: refetchPendingTasks}] = useLazyQuery(getPendingTaskForDashboardQuery);
  const [pendingTask, setPendingTask] = useState({count:0, ids: []});

  useEffect(()=>{
    if(pendingTaskData?.getPendingTaskForDashboard?.response){
      setPendingTask({
        count: pendingTaskData?.getPendingTaskForDashboard?.response?.count,
        ids: pendingTaskData?.getPendingTaskForDashboard?.response?.ids,
      })
    }
  },[pendingTaskData?.getPendingTaskForDashboard?.response]);

  // app usage - of the duties assigned to an employee this period, how many
  // did that employee confirm/clock in via the app vs still sitting unactioned
  const [getAppUsage, {loading: appUsageLoading}] = useLazyQuery(getAppUsageForDashboardQuery);
  const [appUsage, setAppUsage] = useState({usedCount: 0, total: 0, ids: []});


  // guarding hour chart
  const [getTotalguardingDutiesGraph, {data:gurdingChartData, loading: gurdingChartDataLoading, refetch: refetchGurdingChartData}] = useLazyQuery(getTotalguardingDutiesGraphQuery);
  const [totalGuardingDutiesHour, setTotalGuardingDutiesHour] = useState([]);

  useEffect(()=>{
    if(gurdingChartData?.getTotalguardingDutiesGraph?.response){
      setTotalGuardingDutiesHour(gurdingChartData?.getTotalguardingDutiesGraph?.response)
    }
  },[gurdingChartData]);


  // active users chart
  const [getActiveUser, {data:activeUserData, loading: activeUserLoading, refetch: refetchActiveUser}] = useLazyQuery(getActiveUserQuery);
  const [totalActiveUsers, setTotalActiveUsers] = useState(0);
  const [totalUsers, setTotalUsers] = useState(0);

  useEffect(()=>{
    if(activeUserData?.getActiveUser?.response){
      setTotalActiveUsers(activeUserData?.getActiveUser?.response?.active)
      setTotalUsers(activeUserData?.getActiveUser?.response?.users);
    }
  },[activeUserData]);


  // Data Health — unlike every other tile here this isn't scoped to the dashboard's
  // date filter (it's current record state, not something that happened "this week"),
  // so it's fetched once on mount rather than wired into getDateFromFilter/cardFilters.
  // getDataQualityReport already returns the full row list cheaply in one call (it's a
  // handful of indexed finds, not a heavy aggregation), so there's no separate paginated
  // "detail" query the way the other tiles have — the click-through drawer below just
  // reads the same rows already sitting in state instead of re-fetching by id.
  const [getDataQualityReport, { loading: dataQualityLoading }] = useMutation(getDataQualityReportMutation);
  const [dataQualityRows, setDataQualityRows] = useState([]);

  useEffect(()=>{
    (async () => {
      try{
        const { data } = await getDataQualityReport({ variables: { filters: { conditions: [] } } });
        setDataQualityRows(data?.getDataQualityReport?.response || []);
      }catch(err){
        console.log(err);
      }
    })();
  }, []);

  const dataQualityHighCount = dataQualityRows.filter((r) => r?.severity === 'high').length;
  // Dismissal is per-session only (component state, not persisted) - same as
  // SupportBanner's own "hide for now" behaviour, not a permanent user preference.
  const [dataQualityBannerDismissed, setDataQualityBannerDismissed] = useState(false);



 
  const getDateFromFilter = useCallback((filter) => {
    switch(filter){
      case "today":
        return {startDate: dayjs.utc().startOf('day').toDate(), endDate: dayjs.utc().endOf('day').toDate()}
      case "weekly":
        return {
          startDate: dayjs.utc().startOf('isoWeek').startOf('day').toDate(),
          endDate: dayjs.utc().endOf('isoWeek').endOf('day').toDate()
        }
      case "monthly":
        return {
          startDate: dayjs.utc().startOf('month').startOf('day').toDate(),
          endDate: dayjs.utc().endOf('month').endOf('day').toDate()
        }
      case "yearly":
        return {
          startDate: dayjs.utc().startOf('year').startOf('day').toDate(),
          endDate: dayjs.utc().endOf('year').endOf('day').toDate()
        }
      case "custom":
        return {
          startDate: dayjs.utc(customDateRange?.[0]).startOf('day').toDate(),
          endDate: dayjs.utc(customDateRange?.[1]).endOf('day').toDate()
        }
      case "all":
        // no date bounds at all - the backend skips its date filter entirely
        // when startDate/endDate come through undefined
        return { startDate: undefined, endDate: undefined }
    }
  }, [customDateRange]);


  useEffect(()=>{

    getActiveUser({
      fetchPolicy: 'network-only'
    });

  },[]);

  // merges straight off the resolved promise rather than a useEffect watching
  // `data` - see useDashboardTileDetail.js for why (Apollo cache reference
  // stability can otherwise leave this never re-firing on a repeated filter)
  const fetchAppUsage = useCallback(async (filter) => {
    const result = await getAppUsage({
      fetchPolicy: 'network-only',
      variables: { ...getDateFromFilter(filter) },
    });
    const response = result?.data?.getAppUsageForDashboard?.response;
    if (!response) return;
    setAppUsage({ usedCount: response.usedCount || 0, total: response.total || 0, ids: response.ids || [] });
  }, [getDateFromFilter, getAppUsage]);

  useEffect(()=>{
    if (cardFilters.appUsers) {
      fetchAppUsage(cardFilters.appUsers);
    }
  },[cardFilters?.appUsers]);


  useEffect(()=>{
    if(cardFilters.openDuties){
    
      getOpenDuties({
        fetchPolicy: 'network-only',
        variables: {
          ...getDateFromFilter(cardFilters.openDuties),
          status: "0"
        }
      });

    }
  
  },[cardFilters?.openDuties]);

  useEffect(()=>{

    if(cardFilters.confirmedDuties){

      getConfirmDuties({
        fetchPolicy: 'network-only',
        variables: {
          ...getDateFromFilter(cardFilters.confirmedDuties),
          status: "1"
        }
      });


    }

  },[cardFilters?.confirmedDuties]);

  useEffect(()=>{
    if(cardFilters.unconfirmedDuties){
      getUnconfirmedDuties({
        fetchPolicy: 'network-only',
        variables: {
          ...getDateFromFilter(cardFilters.unconfirmedDuties),
          status: "2"
        }
      });
    }
  },[cardFilters?.unconfirmedDuties]);

  useEffect(()=>{

    if(cardFilters.totalHours){

      getTotalHours({
        fetchPolicy: 'network-only',
        variables: {
          ...getDateFromFilter(cardFilters.totalHours),
        }
      });


    }

  },[cardFilters?.totalHours]);

  useEffect(()=>{

    if(cardFilters.guardingDuties){

      getTotalGuardingDuty({
        fetchPolicy: 'network-only',
        variables: {
          ...getDateFromFilter(cardFilters.guardingDuties),
        }
      });


    }

  },[cardFilters?.guardingDuties]);

  useEffect(()=>{

    if(cardFilters.deployedStaff){

      getTotalDeployStaff({
        fetchPolicy: 'network-only',
        variables: {
          ...getDateFromFilter(cardFilters.deployedStaff),
        }
      });


    }

  },[cardFilters?.deployedStaff]);

  useEffect(()=>{

    if(cardFilters.pendingReports){

      getPendingReport({
        fetchPolicy: 'network-only',
        variables: {
          ...getDateFromFilter(cardFilters.pendingReports),
        }
      });


    }

  },[cardFilters?.pendingReports]);

  useEffect(()=>{

    if(cardFilters.pendingTasks){

      getPendingTask({
        fetchPolicy: 'network-only',
        variables: {
          ...getDateFromFilter(cardFilters.pendingTasks),
        }
      });


    }

  },[cardFilters?.pendingTasks]);

  useEffect(()=>{

    if(cardFilters.guardingHoursChart){

      getTotalguardingDutiesGraph({
        fetchPolicy: 'network-only',
        variables: {
          ...getDateFromFilter(cardFilters.guardingHoursChart),
          graphType: cardFilters.guardingHoursChart
        }
      });


    }

  },[cardFilters?.guardingHoursChart]);


  const handleActivityLogDateFilterChange = useCallback((key) => {
    if (key === 'custom') {
      setActiveCardPicker('activityLog');
    } else {
      setCardFilters(prev => ({ ...prev, activityLog: key }));
    }
  }, []);

  // ── tile click-through detail drawer ──
  // one hook instance per backend query; every schedule-based tile (Open/
  // Confirmed/Guarding Duties, Total Hours, Pending Checkpoints) shares
  // scheduleDetail since they all resolve to a set of Schedule ids
  const scheduleDetail = useDashboardTileDetail(getScheduleDashboardDetailQuery, 'getScheduleDashboardDetail');
  const employeeDetail = useDashboardTileDetail(getEmployeeDashboardDetailQuery, 'getEmployeeDashboardDetail');
  const pendingTaskDetail = useDashboardTileDetail(getPendingTaskDetailQuery, 'getPendingTaskDetail');
  const pendingCheckpointDetail = useDashboardTileDetail(getPendingCheckpointDetailQuery, 'getPendingCheckpointDetail');
  const activeUserDetail = useDashboardTileDetail(getActiveUserDetailQuery, 'getActiveUserDetail');

  const [activeTile, setActiveTile] = useState(null);

  const scheduleColumns = [
    { key: 'site', label: 'Site', width: '1.4fr', render: (row) => row?.siteName || '-' },
    { key: 'date', label: 'Date', width: '1.3fr', render: (row) => row?.firstDate ? dayjs(row.firstDate).format('DD MMM YYYY, HH:mm') : '-' },
    { key: 'hours', label: 'Hours', width: '0.7fr', render: (row) => (typeof row?.totalHours === 'number' ? row.totalHours.toFixed(2) : (row?.totalHours || '-')) },
    { key: 'assignee', label: 'Assigned To', width: '1.2fr', render: (row) => row?.employeeName || row?.agencyName || 'Unassigned' },
    {
      key: 'status', label: 'Status', width: '1.3fr',
      render: (row) => (
        <FulfillDutyAction
          row={row}
          onFulfilled={(id, patch) => {
            scheduleDetail.patchRow(id, patch);
            refetchOpenDuties?.();
            refetchConfirmDuties?.();
            refetchUnconfirmedDuties?.();
          }}
        />
      )
    },
  ];

  const employeeColumns = [
    { key: 'name', label: 'Employee', width: '1.6fr', render: (row) => `${row?.firstname || ''} ${row?.lastname || ''}`.trim() || '-' },
    { key: 'email', label: 'Email', width: '1.6fr', render: (row) => row?.email || '-' },
    { key: 'phone', label: 'Phone', width: '1fr', render: (row) => row?.phone || '-' },
  ];

  const pendingTaskColumns = [
    { key: 'task', label: 'Task', width: '1.4fr', render: (row) => row?.label || '-' },
    { key: 'site', label: 'Site', width: '1.2fr', render: (row) => row?.siteName || '-' },
    { key: 'employee', label: 'Assigned To', width: '1.2fr', render: (row) => row?.employeeName || 'Unassigned' },
    { key: 'due', label: 'Due', width: '1.2fr', render: (row) => row?.requiredAt ? dayjs(row.requiredAt).format('DD MMM YYYY, HH:mm') : '-' },
    { key: 'flag', label: '', width: '0.8fr', render: (row) => row?.expired ? <Tag color="red">Expired</Tag> : (row?.late ? <Tag color="orange">Late</Tag> : null) },
  ];

  // checkpoints are configured per-site as either a Form Template or Custom
  // Fields (SiteCheckpoint.responseFlow) - surface that alongside the usual
  // site/assignee/due columns so it reads the same as the site's own checkpoint list
  const checkpointColumns = [
    { key: 'checkpoint', label: 'Checkpoint', width: '1.4fr', render: (row) => row?.label || '-' },
    { key: 'site', label: 'Site', width: '1.2fr', render: (row) => row?.siteName || '-' },
    {
      key: 'type', label: 'Type', width: '1.1fr',
      render: (row) => row?.responseFlow === 'form'
        ? <Tag color="blue">Form Template</Tag>
        : row?.responseFlow === 'fields'
          ? <Tag color="purple">Custom Fields</Tag>
          : '-'
    },
    { key: 'employee', label: 'Assigned To', width: '1.2fr', render: (row) => row?.employeeName || 'Unassigned' },
    { key: 'due', label: 'Due', width: '1.2fr', render: (row) => row?.requiredAt ? dayjs(row.requiredAt).format('DD MMM YYYY, HH:mm') : '-' },
    { key: 'flag', label: '', width: '0.7fr', render: (row) => row?.expired ? <Tag color="red">Expired</Tag> : (row?.late ? <Tag color="orange">Late</Tag> : null) },
  ];

  // "App Usage" reuses the same schedule rows as the duty tiles - status "1"
  // means the assigned employee confirmed/clocked in via the app for this
  // shift, anything else assigned ("2") means they haven't actioned it yet
  const appUsageColumns = [
    { key: 'site', label: 'Site', width: '1.3fr', render: (row) => row?.siteName || '-' },
    { key: 'employee', label: 'Employee', width: '1.3fr', render: (row) => row?.employeeName || 'Unassigned' },
    { key: 'date', label: 'Date', width: '1.3fr', render: (row) => row?.firstDate ? dayjs(row.firstDate).format('DD MMM YYYY, HH:mm') : '-' },
    // real evidence (a completed Clock In, or a mobile-tagged confirm audit
    // entry) - not the duty's status, since admins can force status "1" too
    { key: 'used', label: 'Used App', width: '1fr', render: (row) => row?.usedApp ? <div style={{color:"green"}}>Yes</div> : <div style={{color:"orange"}}>No</div> },
  ];

  const activeUserColumns = [
    { key: 'name', label: 'User', width: '1.6fr', render: (row) => `${row?.firstname || ''} ${row?.lastname || ''}`.trim() || '-' },
    { key: 'email', label: 'Email', width: '1.8fr', render: (row) => row?.email || '-' },
    { key: 'role', label: 'Access', width: '1fr', render: (row) => row?.userAccessType || '-' },
    { key: 'status', label: 'Status', width: '1fr', render: (row) => row?.isActive ? <Tag color="green">Active</Tag> : <Tag>Inactive</Tag> },
    { key: 'lastActive', label: 'Last Active', width: '1.2fr', render: (row) => row?.lastActive ? dayjs(row.lastActive).format('DD MMM YYYY, HH:mm') : '-' },
  ];

  const SEVERITY_TAG_COLOR = { high: 'red', medium: 'orange', low: 'blue' };
  const dataQualityColumns = [
    { key: 'module', label: 'Module', width: '0.9fr', render: (row) => row?.module || '-' },
    { key: 'record', label: 'Record', width: '1.5fr', render: (row) => row?.recordName || '-' },
    { key: 'issue', label: 'Issue', width: '1.8fr', render: (row) => row?.issueLabel || '-' },
    { key: 'severity', label: 'Severity', width: '0.9fr', render: (row) => <Tag color={SEVERITY_TAG_COLOR[row?.severity] || 'default'}>{row?.severity?.toUpperCase() || '-'}</Tag> },
  ];

  // Doesn't use useDashboardTileDetail — that hook's open()/loadMore() drive a paginated
  // "detail" query keyed by ids collected at summary time, which fits a tile whose rows
  // all come from one collection. Data Health's rows span three collections with no
  // single id space, and the full list is already sitting in dataQualityRows from the
  // eager fetch above, so this just adapts that state to the same {rows, loading,
  // hasMore, totalCount, open, loadMore, patchRow} shape DashboardDetailDrawer expects.
  const dataQualityDetail = {
    rows: dataQualityRows,
    loading: dataQualityLoading,
    hasMore: false,
    totalCount: dataQualityRows.length,
    open: () => {},
    loadMore: () => {},
    patchRow: () => {},
  };

  const TILE_META = {
    openDuties: { title: 'Open Duties', hook: scheduleDetail, columns: scheduleColumns, rowHref: dutyRowHref, emptyText: 'No open duties for this period.', getVariables: () => ({ ids: openDuty?.ids || [] }) },
    unconfirmedDuties: { title: 'Unconfirmed Duties', hook: scheduleDetail, columns: scheduleColumns, rowHref: dutyRowHref, emptyText: 'No unconfirmed duties for this period.', getVariables: () => ({ ids: unconfirmedDuty?.ids || [] }) },
    confirmedDuties: { title: 'Confirmed Duties', hook: scheduleDetail, columns: scheduleColumns, rowHref: dutyRowHref, emptyText: 'No confirmed duties for this period.', getVariables: () => ({ ids: confirmDuty?.ids || [] }) },
    guardingDuties: { title: 'Guarding Duties', hook: scheduleDetail, columns: scheduleColumns, rowHref: dutyRowHref, emptyText: 'No guarding duties for this period.', getVariables: () => ({ ids: totalGuardHoursIds || [] }) },
    totalHours: { title: 'Total Hours', hook: scheduleDetail, columns: scheduleColumns, rowHref: dutyRowHref, emptyText: 'No duties for this period.', getVariables: () => ({ ids: totalHoursIds || [] }) },
    pendingReports: { title: 'Pending Checkpoints', hook: pendingCheckpointDetail, columns: checkpointColumns, rowHref: dutyOfRowHref, emptyText: 'No pending checkpoints for this period.', getVariables: () => ({ ids: pendingReport?.ids || [] }) },
    deployedStaff: { title: 'Deployed Staff', hook: employeeDetail, columns: employeeColumns, emptyText: 'No deployed staff for this period.', getVariables: () => ({ ids: totalDeployedStaffIds || [] }) },
    pendingTasks: { title: 'Pending Tasks', hook: pendingTaskDetail, columns: pendingTaskColumns, rowHref: dutyOfRowHref, emptyText: 'No pending tasks for this period.', getVariables: () => ({ ids: pendingTask?.ids || [] }) },
    appUsers: { title: 'App Usage', hook: scheduleDetail, columns: appUsageColumns, rowHref: dutyRowHref, emptyText: 'No shifts assigned for this period.', getVariables: () => ({ ids: appUsage?.ids || [] }) },
    activeUsers: { title: 'Active Users', hook: activeUserDetail, columns: activeUserColumns, emptyText: 'No users found.', getVariables: () => ({}) },
    dataQuality: { title: 'Data Health', hook: dataQualityDetail, columns: dataQualityColumns, emptyText: 'No data quality issues found.', getVariables: () => ({}) },
  };

  const openTileDrawer = (tileKey) => {
    const meta = TILE_META[tileKey];
    if (!meta) return;
    setActiveTile(tileKey);
    meta.hook.open(meta.getVariables());
  };

  const activeTileMeta = activeTile ? TILE_META[activeTile] : null;

  // The metric tiles, in their default order. Each user can drag them into their own order
  // (SortableTiles below, saved per user); tiles they can't see are simply not in the list.
  const renderTiles = () => [
    { key: 'activeUsers', node: (
      <Card className="metric-card tile-clickable" size="small" onClick={() => openTileDrawer('activeUsers')} data-hint="View details">
            <div className="metric-header">
              <span className="metric-label">Active Users</span>
            </div>
            <div className="metric-value">{totalActiveUsers +" / "+ totalUsers}</div>
            {
              totalActiveUsers?
              <div className="avatar-group ">
                <Avatar.Group maxCount={totalActiveUsers} size="small">
                  <div className="post-avatar">
                    <Avatar size={25} style={{ backgroundColor: '#17a2b8' }} src={"https://avatar.iran.liara.run/public/13"} />
                  </div>
                  {/* <div className="emp-avatar">
                    <Avatar size={25} style={{ backgroundColor: '#17a2b8' }} src={"https://avatar.iran.liara.run/public/13"} />
                  </div> */}
                </Avatar.Group>
              </div>
              : null
            }
          </Card>
    ) },
    schedulingEnabled && { key: 'openDuties', node: (
      <Card className="metric-card alert-card tile-clickable" size="small" onClick={() => openTileDrawer('openDuties')} data-hint="View details">
            <div className="metric-header">
              <span className="metric-label">Open duties</span>
              <div onClick={(e) => e.stopPropagation()}>
                <Dropdown overlay={createDateFilterMenu('openDuties')} trigger={['click']}>
                  <span className="metric-badge clickable">
                    {getDateFilterLabel(cardFilters.openDuties)} <DownOutlined style={{ fontSize: '10px' }} />
                  </span>
                </Dropdown>
              </div>
            </div>
            <div className="metric-value">{openDuty?.count}</div>
          </Card>
    ) },
    schedulingEnabled && { key: 'confirmedDuties', node: (
      <Card className="metric-card tile-clickable" size="small" onClick={() => openTileDrawer('confirmedDuties')} data-hint="View details">
            <div className="metric-header">
              <span className="metric-label">Confirmed duties</span>
              <div onClick={(e) => e.stopPropagation()}>
                <Dropdown overlay={createDateFilterMenu('confirmedDuties', true)} trigger={['click']}>
                  <span className="metric-badge clickable">
                    {getDateFilterLabel(cardFilters.confirmedDuties)} <DownOutlined style={{ fontSize: '10px' }} />
                  </span>
                </Dropdown>
              </div>
            </div>
            <div className="metric-value">{confirmDuty?.count}</div>
          </Card>
    ) },
    schedulingEnabled && { key: 'unconfirmedDuties', node: (
      <Card className={`metric-card tile-clickable${unconfirmedDuty?.count ? ' alert-card' : ''}`} size="small" onClick={() => openTileDrawer('unconfirmedDuties')} data-hint="View details">
            <div className="metric-header">
              <span className="metric-label">Unconfirmed duties</span>
              <div onClick={(e) => e.stopPropagation()}>
                <Dropdown overlay={createDateFilterMenu('unconfirmedDuties')} trigger={['click']}>
                  <span className="metric-badge clickable">
                    {getDateFilterLabel(cardFilters.unconfirmedDuties)} <DownOutlined style={{ fontSize: '10px' }} />
                  </span>
                </Dropdown>
              </div>
            </div>
            <div className="metric-value">{unconfirmedDuty?.count}</div>
          </Card>
    ) },
    schedulingEnabled && { key: 'guardingDuties', node: (
      <Card className="metric-card tile-clickable" size="small" onClick={() => openTileDrawer('guardingDuties')} data-hint="View details">
            <div className="metric-header">
              <span className="metric-label">Guarding Duties</span>
              <div onClick={(e) => e.stopPropagation()}>
                <Dropdown overlay={createDateFilterMenu('guardingDuties')} trigger={['click']}>
                  <span className="metric-badge clickable">
                    {getDateFilterLabel(cardFilters.guardingDuties)} <DownOutlined style={{ fontSize: '10px' }} />
                  </span>
                </Dropdown>
              </div>
            </div>
            <div className="metric-value">{totalGuardHours}</div>
            {totalGuardHoursSparkData?.length > 0 ? (
              <ResponsiveContainer width="100%" height={50}>
                <LineChart data={totalGuardHoursSparkData}>

                  <XAxis dataKey="label" hide />
                  <Line type="monotone" dataKey="value" stroke="#17a2b8" strokeWidth={2} dot={false} />
                  <Tooltip
                    formatter={(value) => [`${value}`, "Total Guarding Duties"]}
                    labelFormatter={(label) => `Date: ${label}`}
                  />

                </LineChart>
              </ResponsiveContainer>
            ) : <SparkEmpty />}
          </Card>
    ) },
    schedulingEnabled && { key: 'totalHours', node: (
      <Card className="metric-card tile-clickable" size="small" onClick={() => openTileDrawer('totalHours')} data-hint="View details">
            <div className="metric-header">
              <span className="metric-label">Total Hours</span>
              <div onClick={(e) => e.stopPropagation()}>
                <Dropdown overlay={createDateFilterMenu('totalHours')} trigger={['click']}>
                  <span className="metric-badge clickable">
                    {getDateFilterLabel(cardFilters.totalHours)} <DownOutlined style={{ fontSize: '10px' }} />
                  </span>
                </Dropdown>
              </div>
            </div>
            <div className="metric-value">{totalHours}</div>
            {totalHoursSparkData?.length > 0 ? (
              <ResponsiveContainer width="100%" height={50}>
                <LineChart data={totalHoursSparkData}>
                  <XAxis dataKey="label" hide />
                  <Line type="monotone" dataKey="value" stroke="#17a2b8" strokeWidth={2} dot={false} />
                  <Tooltip
                    formatter={(value) => [`${value} hrs`, "Total Hours"]}
                    labelFormatter={(label) => `Date: ${label}`}
                  />
                </LineChart>
              </ResponsiveContainer>
            ) : <SparkEmpty />}
          </Card>
    ) },
    schedulingEnabled && { key: 'deployedStaff', node: (
      <Card className="metric-card tile-clickable" size="small" onClick={() => openTileDrawer('deployedStaff')} data-hint="View details">
            <div className="metric-header">
              <span className="metric-label">Deployed Staff</span>
              <div onClick={(e) => e.stopPropagation()}>
                <Dropdown overlay={createDateFilterMenu('deployedStaff')} trigger={['click']}>
                  <span className="metric-badge clickable">
                    {getDateFilterLabel(cardFilters.deployedStaff)} <DownOutlined style={{ fontSize: '10px' }} />
                  </span>
                </Dropdown>
              </div>
            </div>
            <div className="metric-value">{totalDeployedStaff}</div>
            {totalDeployedStaffSparkData?.length > 0 ? (
              <ResponsiveContainer width="100%" height={50}>
                <LineChart data={totalDeployedStaffSparkData}>
                  <XAxis dataKey="label" hide />
                  <Line type="monotone" dataKey="value" stroke="#17a2b8" strokeWidth={2} dot={false} />
                  <Tooltip
                    formatter={(value) => [`${value}`, "Total Deployed Staff"]}
                    labelFormatter={(label) => `Date: ${label}`}
                  />
                </LineChart>
              </ResponsiveContainer>
            ) : <SparkEmpty />}
          </Card>
    ) },
    schedulingEnabled && { key: 'pendingReports', node: (
      <Card className="metric-card alert-card tile-clickable" size="small" onClick={() => openTileDrawer('pendingReports')} data-hint="View details">
            <div className="metric-header">
              <span className="metric-label">Pending Checkpoints</span>
              <div onClick={(e) => e.stopPropagation()}>
                <Dropdown overlay={createDateFilterMenu('pendingReports')} trigger={['click']}>
                  <span className="metric-badge clickable">
                    {getDateFilterLabel(cardFilters.pendingReports)} <DownOutlined style={{ fontSize: '10px' }} />
                  </span>
                </Dropdown>
              </div>
            </div>
            <div className="metric-value">{pendingReport?.count}</div>
          </Card>
    ) },
    schedulingEnabled && { key: 'pendingTasks', node: (
      <Card className="metric-card alert-card tile-clickable" size="small" onClick={() => openTileDrawer('pendingTasks')} data-hint="View details">
            <div className="metric-header">
              <span className="metric-label">Pending Tasks</span>
              <div onClick={(e) => e.stopPropagation()}>
                <Dropdown overlay={createDateFilterMenu('pendingTasks')} trigger={['click']}>
                  <span className="metric-badge clickable">
                    {getDateFilterLabel(cardFilters.pendingTasks)} <DownOutlined style={{ fontSize: '10px' }} />
                  </span>
                </Dropdown>
              </div>
            </div>
            <div className="metric-value">{pendingTask?.count}</div>
          </Card>
    ) },
    schedulingEnabled && { key: 'appUsers', node: (
      <Card className="metric-card tile-clickable" size="small" onClick={() => openTileDrawer('appUsers')} data-hint="View details">
            <div className="metric-header">
              <span className="metric-label">App Usage</span>
              <div onClick={(e) => e.stopPropagation()}>
                <Dropdown overlay={createDateFilterMenu('appUsers')} trigger={['click']}>
                  <span className="metric-badge clickable">
                    {getDateFilterLabel(cardFilters.appUsers)} <DownOutlined style={{ fontSize: '10px' }} />
                  </span>
                </Dropdown>
              </div>
            </div>
            <div className="metric-value">{appUsage.usedCount} / {appUsage.total}</div>
          </Card>
    ) },
    showServiceRequestTile && { key: 'serviceRequests', node: (
      <Card className={`metric-card tile-clickable${serviceRequestsPending ? ' alert-card' : ''}`} size="small" onClick={() => setRequestsModal('service')} data-hint="View requests">
            <div className="metric-header">
              <span className="metric-label">Service Requests</span>
              <span className="metric-badge">Pending</span>
            </div>
            <div className="metric-value">{serviceRequestsPending}</div>
          </Card>
    ) },
    showUniformTile && { key: 'uniformRequests', node: (
      <Card className={`metric-card tile-clickable${uniformPending ? ' alert-card' : ''}`} size="small" onClick={() => setRequestsModal('uniform')} data-hint="View requests">
            <div className="metric-header">
              <span className="metric-label">Uniform Requests</span>
              <span className="metric-badge">Pending approval</span>
            </div>
            <div className="metric-value">{uniformPending}</div>
            {uniformToIssue > 0 && <div className="metric-sub" style={{ fontSize: 12, color: '#7c98b6' }}>{uniformToIssue} approved, to issue</div>}
          </Card>
    ) },
  ].filter(Boolean);

  const renderDashboard = () => (
    <div className="dashboard-content">
      <div className="left-panel">
        <SortableTiles tiles={renderTiles()} order={tileOrder} onReorder={saveTileOrder} />

        {schedulingEnabled && (
        <Card className="chart-card" size="small">
          <div className="chart-header">
            <span className="chart-title">Guarding Hours Activity</span>
            <Dropdown overlay={createGraphDateFilterMenu('guardingHoursChart')} trigger={['click']}>
                <span className="metric-badge clickable">
                  {getDateFilterLabel(cardFilters.guardingHoursChart)} <DownOutlined style={{ fontSize: '10px' }} />
                </span>
              </Dropdown>
          </div>

          {totalGuardingDutiesHour?.length > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={totalGuardingDutiesHour}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e8e8e8" />
                <XAxis dataKey="month" tick={{ fill: '#8c8c8c', fontSize: 11 }} />
                <YAxis tick={{ fill: '#8c8c8c', fontSize: 11 }} />
                <Tooltip
                  contentStyle={{
                    background: '#fff',
                    border: '1px solid #d9d9d9',
                    borderRadius: '4px',
                    fontSize: '12px'
                  }}
                />
                <Bar dataKey="hours" fill="#17a2b8" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div style={{ height: 280, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Empty description="No guarding hours data for this period." image={Empty.PRESENTED_IMAGE_SIMPLE} />
            </div>
          )}
        </Card>
        )}
      </div>

      <div className="right-panel">
        <ActivityLogCard
          dateFilter={cardFilters.activityLog}
          dateFilterLabel={getDateFilterLabel(cardFilters.activityLog)}
          onDateFilterChange={handleActivityLogDateFilterChange}
          getDateFromFilter={getDateFromFilter}
        />
      </div>

      {activeCardPicker && (
        <div className="date-picker-overlay">
          <Card className="date-picker-card">
            <div className="date-picker-header">
              <span>Select Date Range</span>
              <Button type="text" size="small" onClick={() => setActiveCardPicker(null)}>✕</Button>
            </div>
            <RangePicker 
              style={{ width: '100%', marginTop: '12px' }} 
              onChange={(dates) => setCustomDateRange(dates)}
            />
            <div className="date-picker-actions">
              <Button size="small" onClick={() => setActiveCardPicker(null)}>Cancel</Button>
              <Button 
                size="small" 
                // className="grid-filed-btn" 
                style={{ borderRadius: 'none' }}
                onClick={() => {
                  if (customDateRange) {
                    setCardFilters(prev => ({ ...prev, [activeCardPicker]: 'custom' }));
                  }
                  setActiveCardPicker(null);
                }}
              >
                Apply
              </Button>
            </div>
          </Card>
        </div>
      )}

      {activeTileMeta && (
        <DashboardDetailDrawer
          open={!!activeTile}
          title={activeTileMeta.title}
          totalCount={activeTileMeta.hook.totalCount}
          onClose={() => setActiveTile(null)}
          columns={activeTileMeta.columns}
          rows={activeTileMeta.hook.rows}
          loading={activeTileMeta.hook.loading}
          hasMore={activeTileMeta.hook.hasMore}
          onLoadMore={activeTileMeta.hook.loadMore}
          emptyText={activeTileMeta.emptyText}
          rowHref={activeTileMeta.rowHref}
        />
      )}
    </div>
  );



  const renderHomepage = () => (
    <div className="homepage-content">
      {/* <div className="homepage-header">
        <h2 className="homepage-title">Home Page</h2>
        <p className="homepage-subtitle">Select a module to get started</p>
      </div> */}
      <div className="menu-tiles">
        {menuItems.map(item => (
          <Card key={item.key} className="menu-tile" hoverable size="small" onClick={async()=> item?.route? navigate(item?.route):  await handelLogout() }>
            <div className="menu-tile-content">
              <div className="menu-tile-icon" style={{ backgroundColor: item.color }}>
                {React.cloneElement(item.icon, { style: { fontSize: '20px', color: 'black' } })}
              </div>
              <div className="menu-tile-title">{item.title}</div>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );

  return (
    <div className="app-container">
      <div className="content-wrapper">
        <DashboardHeader ready={!tenantFlagsLoading} />
        {/* a new company's setup walk-through, until it's finished */}
        <SetupChecklist />

        {/* one dropdown for the standard dashboards, the user's own and what to do with them - above the banners */}
        <DashboardBar
          standard={standardDashboards}
          store={myDashboards}
          activeKey={customDashboard ? customDashboard.id : dashboardView}
          onSelect={selectDashboard}
          access={widgetAccess}
          editing={customizing}
          setEditing={setCustomizing}
          libraryOpen={widgetPanelOpen}
          setLibraryOpen={setWidgetPanelOpen}
        />


        {/* A banner rather than another tile in the grid — data quality issues aren't
            "this week's" activity like the metric tiles below, they're a standing
            condition worth surfacing before anything else on the page. Reuses the same
            openTileDrawer('dataQuality') the tile used to trigger, so the click-through
            drawer/columns/eager fetch above are all unchanged. */}
        {
          !dataQualityLoading && dataQualityRows.length > 0 && !dataQualityBannerDismissed &&
          <div className="data-health-banner" onClick={() => openTileDrawer('dataQuality')}>
            <WarningOutlined className="data-health-banner-icon" style={{ fontSize: '16px', color: '#dc3545', flexShrink: 0 }} />
            <span className="data-health-banner-text">
              <b>{dataQualityRows.length}</b> data quality issue{dataQualityRows.length === 1 ? '' : 's'} found across your records
              {
                dataQualityHighCount > 0 &&
                <> — <b style={{ color: '#dc3545' }}>{dataQualityHighCount}</b> need urgent attention</>
              }
            </span>
            <span className="data-health-banner-action">View Details</span>
            <button
              className="data-health-banner-close"
              onClick={(e) => { e.stopPropagation(); setDataQualityBannerDismissed(true); }}
              title="Dismiss for now"
            >
              <CloseOutlined style={{ fontSize: '12px' }} />
            </button>
          </div>
        }

        {/* holidays this month that still need a pay rate before payroll */}
        <HolidayRatesNotice />

        {/* employees paid below the National Minimum Wage, last month or this month */}
        <MinimumWageNotice />

        {dashboardView === 'custom' ? (
          <DashboardContext.Provider value={widgetContext}>
            <CustomDashboard
              dashboard={customDashboard}
              store={myDashboards}
              access={widgetAccess}
              editing={customizing}
              setEditing={setCustomizing}
              libraryOpen={widgetPanelOpen}
              setLibraryOpen={setWidgetPanelOpen}
              renderWidget={(def, props) => def.render(props)}
            />
            {widgetDetail.drawer}
          </DashboardContext.Provider>
        ) : dashboardView === 'Overview' ? (
          <DashboardContext.Provider value={widgetContext}>
            {overviewDashboard ? (
              <CustomDashboard
                dashboard={overviewDashboard}
                store={overviewStore}
                access={widgetAccess}
                editing={false}
                setEditing={() => {}}
                libraryOpen={false}
                setLibraryOpen={() => {}}
                renderWidget={(def, props) => def.render(props)}
                readOnly
              />
            ) : <Skeleton active />}
            {widgetDetail.drawer}
          </DashboardContext.Provider>
        ) : dashboardView === 'loading' ? (
          <Skeleton active />
        ) : dashboardView === 'Compliance'
          ? <ComplianceDashboard showCompliance={showCompliance} showAppraisals={showAppraisals} showSurveys={showSurveys} />
          : renderDashboard()}

        {showUniformTile && (
          <UniformRequestsModal visible={requestsModal === 'uniform'} onClose={() => setRequestsModal(null)} onChanged={() => { refetchUniformWaiting(); setRequestsTick((t) => t + 1); }} liveTick={requestsTick} />
        )}
        {showServiceRequestTile && (
          <ServiceRequestsModal visible={requestsModal === 'service'} onClose={() => setRequestsModal(null)} onChanged={() => { refetchServiceRequests(); setRequestsTick((t) => t + 1); }} liveTick={requestsTick}
            canModify={dashboardUser?.permission?.ServiceRequest?.modify !== false} />
        )}
      </div>
    </div>
  );
};